import type { StoreResponse } from '@clarity/contract';

/**
 * Platform_Label_Map (mythings-breakdown-reconciliation Req 1): an extensible
 * lookup from a store's `platformKey` to its display labels, replacing the
 * binary `careem ? … : 'Talabat'` ternary that mislabelled a MyThings store as
 * Talabat. Adding a future platform is one map entry, not a new ternary branch.
 */
export interface PlatformLabels {
  /** The platform's display name, e.g. 'Talabat' | 'Careem' | 'MyThings'. */
  platformLabel: string;
  /**
   * The platform's subscriber-delivery program label (e.g. 'Talabat Pro',
   * 'Careem Plus'). MyThings has NO such program, so its value is a benign
   * placeholder that is never rendered: the subscriber-delivery row is a 0 for
   * MyThings (no careemPlus, proDelivery 0) and a 0 row is omitted by the graph.
   */
  subscriberLabel: string;
}

const PLATFORM_LABELS: Readonly<Record<StoreResponse['platformKey'], PlatformLabels>> = {
  talabat: { platformLabel: 'Talabat', subscriberLabel: 'Talabat Pro' },
  careem: { platformLabel: 'Careem', subscriberLabel: 'Careem Plus' },
  mythings: { platformLabel: 'MyThings', subscriberLabel: 'MyThings delivery' },
};

/** The display labels for a store's platform; Talabat is the safe default for an unknown key. */
export function platformLabels(platformKey: StoreResponse['platformKey']): PlatformLabels {
  return PLATFORM_LABELS[platformKey] ?? PLATFORM_LABELS.talabat;
}
